/**
 * Hand-mirrored DTOs for the EMS API.
 *
 * The `pnpm openapi` script regenerates a full `schema.ts` from the live
 * OpenAPI document. For the shell we only need the handful of shapes the
 * pages actually bind to, and keeping them here makes the intent obvious
 * in code review. Fields stay in lock-step with `apps/api/src/ems_api/schemas/`.
 */

export type Whoami = {
  subject: string;
  tenant_id: string;
  username: string | null;
  email: string | null;
  roles: string[];
};

export type Page = {
  limit: number;
  offset: number;
  total: number;
};

export type Site = {
  id: string;
  slug: string;
  display_name: string;
  timezone: string;
  status: string;
  created_at: string;
  updated_at: string;
};

export type SiteListResponse = {
  items: Site[];
  page: Page;
};

export type Meter = {
  id: string;
  slug: string;
  display_name: string;
  kind: string;
  unit: string;
  status: string;
  device_id: string | null;
  created_at: string;
  updated_at: string;
};

export type MeterListResponse = {
  items: Meter[];
  page: Page;
};

export type DeviceKind =
  | "battery_stack"
  | "pcs"
  | "power_meter"
  | "pv_inverter"
  | "ev_charger"
  | "hvac"
  | "other"
  | (string & {});

export type DeviceSummary = {
  id: string;
  slug: string;
  display_name: string;
  kind: DeviceKind;
  status: string;
  meter_count: number;
};

export type SiteDeviceListResponse = {
  items: DeviceSummary[];
  page: Page;
};

export type LatestReading = {
  ts: string;
  value: number | null;
  quality: string;
  source: string;
};

export type DeviceMeter = {
  id: string;
  slug: string;
  display_name: string;
  kind: string;
  unit: string;
  status: string;
  latest: LatestReading | null;
};

export type SiteDevice = {
  id: string;
  slug: string;
  display_name: string;
  kind: DeviceKind;
  status: string;
  site_slug: string;
  created_at?: string;
  updated_at?: string;
  meters: DeviceMeter[];
};

export type Reading = {
  ts: string;
  value: number;
  quality: string;
  source: string;
};

export type ReadingListResponse = {
  meter_id: string;
  unit: string;
  from: string;
  to: string;
  count: number;
  items: Reading[];
};

export type AggregateInterval = "15m" | "1h" | "1d";

export type AggregateBucket = {
  /** Bucket start (UTC ISO timestamp). */
  t: string;
  avg: number | null;
  min: number | null;
  max: number | null;
  count: number;
};

export type AggregateResponse = {
  meter_id: string;
  unit: string;
  interval: AggregateInterval;
  from: string;
  to: string;
  count: number;
  items: AggregateBucket[];
};

/**
 * /v1/alarms payloads.
 *
 * Mirrors `apps/api/src/ems_api/schemas/alarms.py`. Alarms are
 * viewer-readable, while `POST /ack` requires operator/admin. The worker is
 * the only resolve writer for now, so the SPA only lists and acknowledges.
 */
export const ALARM_SEVERITIES = ["info", "warning", "critical"] as const;
export type AlarmSeverity = (typeof ALARM_SEVERITIES)[number];

export const ALARM_STATES = ["open", "acknowledged", "resolved"] as const;
export type AlarmState = (typeof ALARM_STATES)[number];

export type Alarm = {
  id: string;
  site_slug: string;
  device_slug: string | null;
  meter_slug: string | null;
  code: string;
  severity: AlarmSeverity;
  state: AlarmState;
  message: string;
  details: Record<string, unknown> | null;
  opened_at: string;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
};

export type AlarmListResponse = {
  items: Alarm[];
  page: Page;
};

/**
 * /v1/admin/alarm-rules payloads.
 *
 * Admin-owned alarm tuning registry. These rows are exported as
 * `EMS_WORKER_ALARM_RULES` JSON for review; worker DB consumption is a later
 * explicit cutover.
 */
export const ALARM_RULE_STATUSES = ["draft", "ready", "retired"] as const;
export type AlarmRuleStatus = (typeof ALARM_RULE_STATUSES)[number];

export type AdminAlarmRule = {
  id: string;
  name: string;
  code: string;
  severity: AlarmSeverity;
  status: AlarmRuleStatus;
  message: string | null;
  meter_slug: string | null;
  external_id: string | null;
  external_id_prefix: string | null;
  min_value: number | null;
  max_value: number | null;
  stale_after_seconds: number | null;
  gap_after_seconds: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

export type AdminAlarmRuleListResponse = {
  items: AdminAlarmRule[];
  page: Page;
};

export type AdminAlarmRuleCreateBody = {
  name: string;
  code: string;
  severity?: AlarmSeverity;
  status?: AlarmRuleStatus;
  message?: string | null;
  meter_slug?: string | null;
  external_id?: string | null;
  external_id_prefix?: string | null;
  min_value?: number | null;
  max_value?: number | null;
  stale_after_seconds?: number | null;
  gap_after_seconds?: number | null;
  notes?: string | null;
};

export type AdminAlarmRuleUpdateBody = Partial<AdminAlarmRuleCreateBody>;

export type AdminAlarmRuleExportResponse = {
  env_var: "EMS_WORKER_ALARM_RULES";
  status: "ready";
  rule_count: number;
  rules_json: string;
};

/**
 * /v1/admin/meters payloads.
 *
 * Mirrors `apps/api/src/ems_api/schemas/admin_meters.py`. The DTOs are kept
 * narrow on purpose: `slug` and `site_slug` are immutable and `device_slug`
 * has its own dedicated PATCH endpoint (§9.3), so the update body only
 * carries the four mutable fields.
 */
export type AdminMeter = {
  id: string;
  site_slug: string;
  device_slug: string | null;
  slug: string;
  display_name: string;
  kind: string;
  unit: string;
  status: string;
  created_at: string;
  updated_at: string;
};

export type AdminMeterListResponse = {
  items: AdminMeter[];
  page: Page;
};

export type AdminMeterCreateBody = {
  site_slug: string;
  device_slug?: string | null;
  slug: string;
  display_name: string;
  kind: string;
  unit: string;
  status?: AdminMeterStatus;
};

export type AdminMeterUpdateBody = {
  display_name?: string;
  kind?: string;
  unit?: string;
  status?: AdminMeterStatus;
};

// Free-form on the API side (validated only at the layer); we surface them
// as Combobox suggestions and let operators type a new value if they need it.
export const METER_KIND_SUGGESTIONS = [
  "electricity",
  "gas",
  "heat",
  "water",
  "temperature",
  "storage",
] as const;

export const METER_UNIT_SUGGESTIONS = [
  "kW",
  "kWh",
  "m3",
  "m3/h",
  "degC",
  "%",
  "Hz",
  "V",
  "A",
] as const;

// Status, on the other hand, is validated server-side against this exact set.
export const METER_STATUSES = ["active", "commissioning", "retired"] as const;
export type AdminMeterStatus = (typeof METER_STATUSES)[number];

/**
 * /v1/admin/devices payloads.
 *
 * Mirrors `apps/api/src/ems_api/schemas/devices.py`. The shape echoes
 * `AdminMeter` deliberately so the admin pages share the same table /
 * modal / delete-confirm shape; the differences are scoped:
 *   - `status` is validated against `{active, retired}` only (devices
 *     don't have a `commissioning` lifecycle like meters do),
 *   - there is no `unit` (devices group channels; the unit lives on
 *     each meter),
 *   - `meter_count` is reported on every list row so the table can
 *     render "B Stack 0/0 — 4 meters" without a second round-trip,
 *   - `slug` and `site_slug` are immutable on PATCH (re-homing a
 *     device is delete + recreate, by API design — see §9.3).
 */
export type AdminDevice = {
  id: string;
  site_slug: string;
  slug: string;
  display_name: string;
  kind: string;
  status: string;
  meter_count: number;
  created_at: string;
  updated_at: string;
};

export type AdminDeviceListResponse = {
  items: AdminDevice[];
  page: Page;
};

export type AdminDeviceCreateBody = {
  site_slug: string;
  slug: string;
  display_name: string;
  kind: string;
  status?: AdminDeviceStatus;
};

export type AdminDeviceUpdateBody = {
  display_name?: string;
  kind?: string;
  status?: AdminDeviceStatus;
};

// Free-form on the API side, surfaced as suggestions only — operators
// can introduce a new device kind (boiler, heat_pump, …) by typing it
// in without a migration. Stays in lockstep with `DEVICE_KIND_SUGGESTIONS`
// in the API schema and with `getDeviceKindMeta` in `deviceKinds.ts`.
export const DEVICE_KIND_SUGGESTIONS = [
  "battery_stack",
  "pcs",
  "power_meter",
  "pv_inverter",
  "ev_charger",
  "hvac",
  "other",
] as const;

// Validated server-side against this exact set (see `_VALID_STATUS`
// in `schemas/devices.py`). Note: shorter than `METER_STATUSES` —
// devices have no `commissioning` lifecycle.
export const DEVICE_STATUSES = ["active", "retired"] as const;
export type AdminDeviceStatus = (typeof DEVICE_STATUSES)[number];

/**
 * /v1/admin/ev-dc-modbus-targets payloads.
 *
 * Mirrors `apps/api/src/ems_api/schemas/ev_dc_modbus_targets.py`. The
 * registry is admin-only configuration storage for EV DC Modbus connector
 * targets; the poller does not consume these rows yet.
 */
export const EV_DC_MODBUS_PROFILES = ["nidec", "cjnoo-gun1", "cjnoo-gun2"] as const;
export type EvDcModbusProfile = (typeof EV_DC_MODBUS_PROFILES)[number];

export type EvDcModbusTarget = {
  id: string;
  site_slug: string;
  device_slug: string;
  name: string;
  host: string;
  port: number;
  unit_id: number;
  profile: EvDcModbusProfile;
  external_id_prefix: string;
  address_offset: number;
  timeout_seconds: number;
  writes_enabled: boolean;
  dlm_safe_current_a: number;
  dlm_comm_timeout_s: number;
  verify_readback_delay_ms: number;
  verify_retries: number;
  verify_tolerance: number;
  created_at: string;
  updated_at: string;
};

export type EvDcModbusTargetListResponse = {
  items: EvDcModbusTarget[];
  page: Page;
};

export type EvDcModbusTargetPollerExportItem = {
  name: string;
  host: string;
  port: number;
  unit_id: number;
  profile: EvDcModbusProfile;
  external_id_prefix: string;
  address_offset: number;
  timeout_seconds: number;
  site_slug: string;
  device_slug: string;
  writes_enabled: boolean;
  dlm_safe_current_a: number;
  dlm_comm_timeout_s: number;
  verify_readback_delay_ms: number;
  verify_retries: number;
  verify_tolerance: number;
};

export type EvDcModbusTargetPollerExportResponse = {
  env_var: "EMS_POLLER_EV_DC_MODBUS_TARGETS";
  include_write_flags: boolean;
  target_count: number;
  page: Page;
  targets: EvDcModbusTargetPollerExportItem[];
  poller_targets_json: string;
};

export type EvDcModbusTargetCreateBody = {
  site_slug: string;
  device_slug: string;
  name: string;
  host: string;
  port?: number;
  unit_id?: number;
  profile?: EvDcModbusProfile;
  external_id_prefix?: string | null;
  address_offset?: number;
  timeout_seconds?: number;
  writes_enabled?: boolean;
  dlm_safe_current_a?: number;
  dlm_comm_timeout_s?: number;
  verify_readback_delay_ms?: number;
  verify_retries?: number;
  verify_tolerance?: number;
};

export type EvDcModbusTargetUpdateBody = {
  name?: string;
  host?: string;
  port?: number;
  unit_id?: number;
  profile?: EvDcModbusProfile;
  external_id_prefix?: string | null;
  address_offset?: number;
  timeout_seconds?: number;
  dlm_safe_current_a?: number;
  dlm_comm_timeout_s?: number;
  verify_readback_delay_ms?: number;
  verify_retries?: number;
  verify_tolerance?: number;
};

/**
 * /v1/admin/notification-settings payloads.
 *
 * Secret values are write-only on update. Reads only return whether a saved
 * password/token exists, matching the Admin UI's masked credential behavior.
 */
export type NotificationTlsMinVersion = "TLS12" | "TLS13";

export type AdminNotificationSettings = {
  email_enabled: boolean;
  smtp_host: string;
  smtp_port: number;
  from_address: string;
  recipients: string;
  auth_username: string;
  smtp_auth_password_set: boolean;
  require_tls: boolean;
  tls_min_version: NotificationTlsMinVersion;
  telegram_enabled: boolean;
  telegram_chat_id: string;
  telegram_bot_token_set: boolean;
  telegram_warning_silent: boolean;
  updated_at: string | null;
};

export type AdminNotificationSettingsUpdateBody = {
  smtp_host?: string;
  smtp_port?: number;
  from_address?: string;
  recipients?: string;
  auth_username?: string;
  smtp_auth_password?: string;
  clear_smtp_auth_password?: boolean;
  require_tls?: boolean;
  tls_min_version?: NotificationTlsMinVersion;
  telegram_chat_id?: string;
  telegram_bot_token?: string;
  clear_telegram_bot_token?: boolean;
  telegram_warning_silent?: boolean;
};

export type AdminNotificationTestStatus = "sent" | "skipped" | "failed";

export type AdminNotificationTestChannelResult = {
  status: AdminNotificationTestStatus;
  message: string;
};

export type AdminNotificationTestResult = {
  ok: boolean;
  email: AdminNotificationTestChannelResult;
  telegram: AdminNotificationTestChannelResult;
};

/**
 * /v1/ev-charger-commands payloads.
 *
 * Mirrors `apps/api/src/ems_api/schemas/ev_charger_commands.py`. The list
 * surface is viewer-readable; create + transition both require operator.
 *
 * The supported `command_type` values are the same allowlist the API
 * validates against (`EV_CHARGER_COMMAND_SPECS`); register addresses and
 * value ranges are surfaced through `safety_min_value` / `safety_max_value`
 * on each row so the UI can render the limits the operator actually
 * committed to without re-deriving them.
 */
export const EV_CHARGER_COMMAND_TYPES = [
  "set_dc_charging_current_limit",
  "set_dc_charging_power_limit",
  "set_hems_current_limit",
] as const;
export type EvChargerCommandType = (typeof EV_CHARGER_COMMAND_TYPES)[number];

export const EV_CHARGER_COMMAND_STATES = [
  "pending",
  "dispatched",
  "succeeded",
  "failed",
  "cancelled",
] as const;
export type EvChargerCommandState = (typeof EV_CHARGER_COMMAND_STATES)[number];

/** Spec table mirrored from the API for UI bounds + units. */
export const EV_CHARGER_COMMAND_SPECS: Record<
  EvChargerCommandType,
  { register_address: number; unit: string; min: number; max: number; description: string }
> = {
  set_dc_charging_current_limit: {
    register_address: 998,
    unit: "A",
    min: 1,
    max: 250,
    description: "DC charging current limit",
  },
  set_dc_charging_power_limit: {
    register_address: 999,
    unit: "kW",
    min: 1,
    max: 360,
    description: "DC charging power limit",
  },
  set_hems_current_limit: {
    register_address: 1000,
    unit: "A",
    min: 1,
    max: 250,
    description: "HEMS current limit",
  },
};

export type EvChargerCommand = {
  id: string;
  site_slug: string;
  device_slug: string;
  command_type: EvChargerCommandType;
  register_address: number;
  value: number;
  unit: string;
  safety_min_value: number;
  safety_max_value: number;
  state: EvChargerCommandState;
  requested_by: string;
  requested_at: string;
  reason: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
};

export type EvChargerCommandListResponse = {
  items: EvChargerCommand[];
  page: Page;
};

export type EvChargerCommandCreateBody = {
  site_slug: string;
  device_slug: string;
  command_type: EvChargerCommandType;
  value: number;
  reason?: string | null;
};

export type EvChargerCommandTransitionBody = {
  state: EvChargerCommandState;
  last_error?: string | null;
};
